/** The frame every map is drawn in: base style, zoom controls, and app-zoom-safe measuring. */
import React, { RefObject, useRef } from "react"

import { AlertTriangle } from "lucide-react"
import "mapbox-gl/dist/mapbox-gl.css"
import Map, {
  Layer,
  type MapProps,
  type MapRef,
  NavigationControl,
  Source,
} from "react-map-gl/mapbox"

import { useMapAutoResize } from "@/hooks/useMapAutoResize"
import type { segmentLines } from "@/lib/mapGeometry"
import { cn } from "@/lib/utils"

export const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN

export const MAP_PANEL = "rounded-lg border border-palette-jungleGreen200 bg-white/95"

type MapShellProps = Omit<MapProps, "mapStyle" | "mapboxAccessToken" | "style"> & {
  mapRef: RefObject<MapRef>
  children?: React.ReactNode
  overlay?: React.ReactNode
}

export function MapShell({ mapRef, children, overlay, ...mapProps }: MapShellProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  useMapAutoResize(mapRef, containerRef)

  return (
    // `mapbox-zoom-shim` so Mapbox measures correctly under the app zoom. See index.css.
    <div ref={containerRef} className="mapbox-zoom-shim absolute inset-0">
      <Map
        ref={mapRef}
        {...mapProps}
        mapStyle="mapbox://styles/mapbox/light-v11"
        mapboxAccessToken={MAPBOX_TOKEN}
        style={{ width: "100%", height: "100%" }}
      >
        <NavigationControl position="top-right" />
        {children}
      </Map>
      {overlay}
    </div>
  )
}

export function MapUnavailable({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "flex flex-1 items-center justify-center bg-palette-jungleGreen000 p-6 text-center",
        className,
      )}
    >
      <div>
        <AlertTriangle className="mx-auto mb-2 h-10 w-10 text-amber-500" />
        <p className="text-sm text-palette-jungleGreen600">Map unavailable (no Mapbox token)</p>
      </div>
    </div>
  )
}

/** Styled as on the timeline: dashed grey internal, red empty, grey loaded. */
export function RouteLine({ id, data }: { id: string; data: ReturnType<typeof segmentLines> }) {
  return (
    <Source id={id} type="geojson" data={data}>
      <Layer
        id={`${id}-border`}
        type="line"
        paint={{
          "line-color": "#e5e7eb",
          "line-width": 6,
          "line-opacity": 0.9,
        }}
      />
      <Layer
        id={`${id}-layer`}
        type="line"
        paint={{
          "line-color": [
            "match",
            ["get", "lineStyle"],
            "dashed_grey",
            "#9ca3af",
            "red",
            "#fecaca",
            "grey",
            "#d1d5db",
            "#d1d5db",
          ],
          "line-dasharray": [
            "match",
            ["get", "lineStyle"],
            "dashed_grey",
            ["literal", [2, 2]],
            ["literal", [1, 0]],
          ],
          "line-width": 4,
          "line-opacity": 0.9,
        }}
      />
    </Source>
  )
}
